'use client';

import { XIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { ICON_SIZE } from '../../lib/header-constants';
import {
  SearchCombobox,
  type SearchComboboxConfig,
  type SearchComboboxItem,
} from '@/shared/components/search-combobox';
import { cn } from '@/shared/utils/cn';

interface HeaderMobileSearchSheetProps {
  isOpen: boolean;
  offsetTop: number;
  search: SearchComboboxConfig;
  onClose: () => void;
}

const HeaderMobileSearchSheet = ({
  isOpen,
  offsetTop,
  search,
  onClose,
}: HeaderMobileSearchSheetProps) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  const handleSubmit = useCallback<SearchComboboxConfig['onSubmit']>(
    (event, query) => {
      onClose();
      search.onSubmit(event, query);
    },
    [onClose, search],
  );

  const handleSelect = useCallback(
    (item: SearchComboboxItem) => {
      onClose();
      search.onPreviewSelect(item);
    },
    [onClose, search],
  );

  const sheetSearch = useMemo<SearchComboboxConfig>(
    () => ({
      ...search,
      onPreviewSelect: handleSelect,
      onSubmit: handleSubmit,
    }),
    [handleSelect, handleSubmit, search],
  );

  if (!isMounted) return null;

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className={cn(
          'global-header-mobile-backdrop bg-adipa-text-primary/15 duration-adipa-base fixed inset-0 transition-opacity ease-in-out',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />

      <div
        aria-label="Buscador"
        aria-modal="true"
        className={cn(
          'global-header-mobile-search-sheet bg-adipa-surface shadow-adipa-console-panel fixed left-0 right-0 top-0 flex flex-col',
          'duration-adipa-base transition-transform ease-in-out',
          isOpen
            ? 'global-header-search-sheet-open'
            : 'global-header-search-sheet-closed pointer-events-none',
        )}
        inert={!isOpen}
        role="dialog"
        style={
          {
            '--global-header-mobile-search-offset': `${offsetTop}px`,
            top: `${offsetTop}px`,
          } as React.CSSProperties
        }
      >
        <div className="border-adipa-border flex items-center gap-2 border-b px-3 py-3">
          <button
            aria-label="Cerrar buscador"
            className={cn(
              'rounded-adipa-lg text-adipa-text-secondary inline-flex h-10 w-10 shrink-0 items-center justify-center',
              'duration-adipa-base hover:bg-adipa-surface-muted hover:text-adipa-primary transition-colors ease-in-out',
              'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
            )}
            type="button"
            onClick={onClose}
          >
            <XIcon size={ICON_SIZE} weight="bold" />
          </button>
          <div className="min-w-0 flex-1">
            <SearchCombobox autoFocus={isOpen} dropdownPlacement="inline" search={sheetSearch} />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default HeaderMobileSearchSheet;
